/**
 * Lottie Layer Renderer
 * Renders Lottie animations using @lottiefiles/dotlottie-react
 */

import React, { useRef, useEffect, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { LottieLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';

interface LottieLayerRendererProps {
  layer: LottieLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function LottieLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: LottieLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const {
    src,
    data,
    autoplay = true,
    loop = true,
    speed = 1,
    // direction = 1,
    // segment,
    // renderer = 'svg',
    hover,
    playOnClick,
  } = content;

  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isHovering, setIsHovering] = useState(false);
  const dotLottieRef = useRef<any>(null);

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 200);
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 200);

  // Handle hover play behavior
  useEffect(() => {
    if (hover) {
      if (isHovering) {
        dotLottieRef.current?.play();
      } else {
        dotLottieRef.current?.pause();
      }
    }
  }, [hover, isHovering]);

  const handleClick = (e: React.MouseEvent) => {
    if (playOnClick) {
      setIsPlaying(!isPlaying);
      if (isPlaying) {
        dotLottieRef.current?.pause();
      } else {
        dotLottieRef.current?.play();
      }
    }
    onClick?.(e);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    onMouseLeave?.();
  };

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: layer.locked ? 'default' : 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
    overflow: 'hidden',
  };

  // Need either src or data
  if (!src && !data) {
    return (
      <div
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px dashed rgba(255,255,255,0.2)',
        }}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-layer-id={layer.id}
      >
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          No Lottie source
        </span>
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-layer-id={layer.id}
    >
      <DotLottieReact
        dotLottieRefCallback={(ref) => {
          dotLottieRef.current = ref;
        }}
        src={src}
        data={data as any}
        autoplay={hover ? false : autoplay}
        loop={loop === true || (typeof loop === 'number' && loop > 1)}
        speed={speed}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}
